import { useLazyQuery } from '@apollo/client'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { Auth, Hub } from 'aws-amplify'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPixel from 'react-facebook-pixel'
import { useIntercom } from 'react-use-intercom'

import { authService } from '../../lib/auth-service'
import * as queries from './queries'
import { UserContext } from './user-context'

const options = {
    autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
    debug: false, // enable logs
}

interface UserProviderProps {
    children: React.ReactNode
    testEnv?: boolean
}

/**
 * Provider to allow easy access to the use object from aws
 */
export function UserProvider({ children, testEnv = false }: UserProviderProps) {
    const { boot } = useIntercom()
    const [user, setUserState] = useState<CognitoUser | null>()
    const [query, { data: userProfileData, loading }] = useLazyQuery(
        queries.GET_USER_PROFILE,
        {
            variables: {
                id: user && user.getUsername ? user.getUsername() : undefined,
            },
            fetchPolicy: 'network-only',
        },
    )
    const userProfile = useMemo(
        () => userProfileData ?? undefined,
        [userProfileData],
    )

    useEffect(() => {
        //@ts-ignore
        if (user && user.attributes && !testEnv && userProfile) {
            boot({
                alignment: 'right',
                //@ts-ignore
                email: user?.attributes?.email,
                name: userProfile?.getUserProfile?.firstName,
                //@ts-ignore
                userId: user?.attributes?.sub,
                //@ts-ignore
                phone: user?.attributes?.phone,
            })

            Auth.currentAuthenticatedUser({
                bypassCache: true,
            })
            const advancedMatching: any = {
                //@ts-ignore
                em: user.attributes.email,
                external_id: user.getUsername(),
            }
            ReactPixel.init(
                //@ts-ignore
                process.env.REACT_APP_FB_PIXEL_ID,
                advancedMatching,
                options,
            )
        }
    }, [user, testEnv, userProfile, boot])

    useEffect(() => {
        if (user && !testEnv) {
            query()
        }
    }, [user, testEnv, query])

    const setUser = useCallback((user: CognitoUser) => {
        setUserState(user)
    }, [])

    const clearUser = useCallback(() => {
        setUserState(null)
    }, [])

    async function getUser() {
        try {
            const user = await authService.getUser()
            setUserState(user)
        } catch (err) {
            setUserState(null)
        }
    }

    useEffect(() => {
        // get user on startup
        getUser()

        // No need to call Hub.remove as this provider wraps the entire app.
        // If that changes use a cleanup function to call Hub.remove
        Hub.listen('auth', ({ payload: { event } }) => {
            switch (event) {
                case 'signIn':
                    getUser()
                    break
                case 'signOut':
                    clearUser()
                    break
            }
        })
    }, [clearUser])

    const context = {
        user,
        userProfile,
        setUser,
        clearUser,
        loading,
        getUser: query,
        getCognitoUser: getUser,
    }

    return (
        <UserContext.Provider value={context}>{children}</UserContext.Provider>
    )
}
